<template>
<global-modal v-model="isOpened" width="680px">
    <save-form
        ref="saveForm"
        v-bind="props"
        :savedUrl="savedUrl"
        :projects="projects"
        :projects-enabled="projectsEnabled"
        :details-state="detailsState"
        :can-save-details="canSaveDetails"
        :selected-project-id="selectedProjectId"
        :details-url="detailsUrl"
        :details-name="detailsName"
        @submit="submit"
        @continue="continueWithoutSaving"
        @login="openLogin"
        @save-details="saveDetails"
    />
</global-modal>
</template>

<script setup lang="ts">
import {SaveForm, GlobalModal, SaveFormTypeEnum, CountdownTexts} from '@webstacz/shop-components';
import {Reactive, nextTick, reactive, ref, useTemplateRef, watch} from "vue";
import axios from '@/plugins/axios';
import {useI18n} from "vue-i18n";

export type Props = {
    prefillEmail?: string;
    isLoggedIn?: boolean;
    woodSamplesUrl?: string;
    appointmentUrl?: string;
    type?: SaveFormTypeEnum;
    requestType?: string;
    showContinueButton?: boolean;
    allowAttachments?: boolean;
    countdownConfig?: {
        dateTo: string;
        texts: CountdownTexts;
        backgroundColor: string;
        textColor: string;
        isResponsive?: boolean;
    }
};

type Project = { id: number; name: string };

const props: Reactive<Props> = reactive({})
const {t} = useI18n();

const saveFormRef = useTemplateRef<typeof SaveForm>('saveForm');

const isOpened = ref(false);
const savedUrl = ref('');
const projects = ref<Project[]>([]);
const projectsEnabled = ref(true);
const detailsState = ref<'' | 'saving' | 'saved'>('');
const canSaveDetails = ref(false);
const selectedProjectId = ref<number | null>(null);
const detailsUrl = ref('');
const detailsName = ref('');
let updateUrl = '';
let claimToken = '';
let submitCallback: Parameters<typeof window.openSaveModal>[1] = () => Promise.resolve('');
let continueCallback: Parameters<typeof window.openSaveModal>[2] = () => Promise.resolve();

/**
 * Closing normally throws the success screen away. While the visitor is off
 * signing in the modal is only stepping aside, so everything it holds — above
 * all the saved URL the identifier is read from — has to survive.
 */
let isSteppingAsideForAuth = false;

watch(isOpened, () => {
    if (isSteppingAsideForAuth) {
        return;
    }
    if (saveFormRef.value) {
        saveFormRef.value.resetForm();
    }
    savedUrl.value = '';
    projects.value = [];
    detailsState.value = '';
    canSaveDetails.value = false;
    selectedProjectId.value = null;
    detailsUrl.value = '';
    detailsName.value = '';
    updateUrl = '';
    claimToken = '';
});

window.openSaveModal = (config, submitCb, continueCb = () => Promise.resolve()): void => {
    Object.assign(props, config);
    // the login config is baked into cached HTML and therefore only carries the
    // shop wide default; the customer's own setting rides along with the auth state
    projectsEnabled.value = window.customerAuth?.projectsEnabled
        ?? (loginConfig()?.projectsEnabled !== false);
    if (window.customerAuth?.isAuthenticated && window.customerAuth.email) {
        props.prefillEmail = window.customerAuth.email;
        props.isLoggedIn = true;
    } else {
        props.isLoggedIn = false;
    }
    submitCallback = submitCb;
    continueCallback = continueCb;
    isOpened.value = true;
};

const submit = async (form: any) => {
    try {
        const result = await submitCallback(form);
        savedUrl.value = typeof result === 'string' ? result : (result?.url ?? '');
        claimToken = typeof result === 'string' ? '' : (result?.claimToken ?? '');

        if (savedUrl.value && props.isLoggedIn) {
            loadDetailOptions(identifierFromUrl(savedUrl.value));
        }
    } catch (e) {
        console.error(e);
        saveFormRef.value?.showError(t('attachment-upload-error-send'));
    }
}

/**
 * The configuration identifier is the last segment of the saved URL. Every
 * caller produces that URL, while the numeric id only reaches the modal from
 * the callers whose submit callback happens to return it.
 *
 * @param {string} url
 * @returns {string}
 */
const identifierFromUrl = (url: string): string => {
    try {
        return new URL(url, window.location.origin).pathname.split('/').filter(Boolean).pop() ?? '';
    } catch (e) {
        return '';
    }
}

/**
 * The projects and the write address for the configuration that was just saved.
 * Only ever called for a logged-in visitor — the endpoint sits behind the
 * customers guard, so a guest simply gets nothing to fill in.
 *
 * @param {string} identifier
 * @returns {Promise<void>}
 */
const loginConfig = (): any => {
    const userArea = document.querySelector('.user-area[data-login-config]');

    return JSON.parse(userArea?.getAttribute('data-login-config') ?? '{}');
}

/**
 * @param {object} data
 * @returns {void}
 */
const applyDetailOptions = (data: any): void => {
    projects.value = data?.projects ?? [];
    updateUrl = data?.updateUrl ?? '';
    canSaveDetails.value = updateUrl !== '';
    // the configuration is already stored under a name when the caller sent one,
    // so the rename field starts from what is on the record rather than empty
    detailsName.value = data?.name ?? '';
    selectedProjectId.value = data?.projectId ?? null;
}

/**
 * Moves the configuration onto the account that was just signed into. A guest
 * files it under the e-mail typed in the form, which need not be the address
 * they log in with — without this the modal would have nothing to offer and
 * would silently drop the naming step.
 *
 * @returns {Promise<boolean>} whether the options are now loaded
 */
const claimConfiguration = async (): Promise<boolean> => {
    const config = loginConfig();
    if (claimToken === '' || !config?.urls?.configurationClaim) {
        return false;
    }

    try {
        const {data} = await axios.post(config.urls.configurationClaim, {token: claimToken});
        applyDetailOptions(data);

        return canSaveDetails.value;
    } catch (e) {
        return false;
    }
}

const loadDetailOptions = async (identifier: string): Promise<void> => {
    if (identifier === '') {
        return;
    }

    try {
        const config = loginConfig();
        if (!config?.urls?.configurationOptions) {
            return;
        }

        const {data} = await axios.get(config.urls.configurationOptions, {params: {identifier}});
        applyDetailOptions(data);
    } catch (e) {
        applyDetailOptions(null);
    }
}

/**
 * A projectName means the customer asked for a project that does not exist yet;
 * the endpoint creates it and answers with the refreshed list plus the project
 * the configuration ended up in, so the picker can show it as selected.
 *
 * @param {object} payload
 * @returns {Promise<void>}
 */
const saveDetails = async (payload: { name: string; projectId: number | ''; projectName: string }) => {
    if (!updateUrl) {
        return;
    }

    detailsState.value = 'saving';
    try {
        const {data} = await axios.put(updateUrl, {
            name: payload.name,
            project_id: payload.projectId === '' ? null : payload.projectId,
            project_name: payload.projectName || null,
        });
        projects.value = data?.projects ?? projects.value;
        selectedProjectId.value = data?.projectId ?? null;
        detailsUrl.value = data?.detailsUrl ?? '';
        detailsState.value = 'saved';
    } catch (e) {
        detailsState.value = '';
    }
}

const continueWithoutSaving = async () => {
    await continueCallback();
    isOpened.value = false;
}

/**
 * Hands over to the login modal without losing the saved configuration, so the
 * visitor lands back on the same success screen afterwards.
 *
 * @param {string} email
 * @returns {void}
 */
const stepAsideForAuth = (email: string): void => {
    isSteppingAsideForAuth = true;
    isOpened.value = false;
    window.openLoginModal?.({prefillEmail: email, redirectAfterLogin: false});
}

const openLogin = (payload: { email: string }) => stepAsideForAuth(payload.email);

/**
 * Signing in is the only thing that fires this, so the visitor is now the owner
 * of the configuration they just saved: claim it for the account and land in
 * the saved configurations section, where it is listed.
 */
document.addEventListener('customerAuthChanged', async () => {
    if (!isSteppingAsideForAuth) {
        return;
    }

    if (savedUrl.value) {
        await claimConfiguration();
    }

    const accountUrl = loginConfig()?.urls?.accountProducts;

    if (accountUrl) {
        window.location.assign(accountUrl);
        return;
    }

    props.isLoggedIn = true;
    isOpened.value = true;
    await nextTick();
    isSteppingAsideForAuth = false;

    if (savedUrl.value && !canSaveDetails.value) {
        await loadDetailOptions(identifierFromUrl(savedUrl.value));
    }
});
</script>

<style module lang="scss">

</style>
